import React from 'react';
import { motion } from 'framer-motion';
import { Play, ExternalLink, Save, AlertCircle } from 'lucide-react';

interface VideoStepProps {
  videoUrl: string;
  title: string;
  onChange: (data: { videoUrl: string; title: string }) => void;
  onSave?: () => void;
}

const VideoStep: React.FC<VideoStepProps> = ({ 
  videoUrl, 
  title, 
  onChange, 
  onSave 
}) => {

  const getVideoId = (url: string): string | null => {
    const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const vimeoRegex = /vimeo\.com\/(?:.*\/)?(\d+)/;
    
    const youtubeMatch = url.match(youtubeRegex);
    const vimeoMatch = url.match(vimeoRegex);
    
    if (youtubeMatch) return youtubeMatch[1];
    if (vimeoMatch) return vimeoMatch[1];
    return null;
  };

  const getVideoType = (url: string): 'youtube' | 'vimeo' | null => {
    if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube';
    if (url.includes('vimeo.com')) return 'vimeo';
    return null;
  };

  const getEmbedUrl = (url: string): string | null => {
    const videoId = getVideoId(url);
    const videoType = getVideoType(url);
    
    if (!videoId || !videoType) return null;
    
    if (videoType === 'youtube') {
      return `https://www.youtube.com/embed/${videoId}`;
    } else if (videoType === 'vimeo') {
      return `https://player.vimeo.com/video/${videoId}`;
    }
    
    return null;
  };

  const embedUrl = getEmbedUrl(videoUrl);
  const isValidUrl = !!embedUrl;

  return (
    <motion.div
      className="bg-white rounded-lg border border-gray-200 overflow-hidden"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-[#667eea] to-[#764ba2] px-6 py-4 text-white">
        <div className="flex items-center space-x-2">
          <Play className="w-5 h-5" />
          <h3 className="font-semibold">Video Step</h3>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Video URL Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Video URL
          </label>
          <div className="flex space-x-2">
            <input
              type="url"
              value={videoUrl}
              onChange={(e) => onChange({ videoUrl: e.target.value, title })}
              placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#667eea] focus:border-transparent"
            />
            {videoUrl && (
              <a
                href={videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
          {videoUrl && !isValidUrl && (
            <div className="flex items-center space-x-2 mt-2 text-red-600">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">Please enter a valid YouTube or Vimeo URL</span>
            </div>
          )}
        </div>

        {/* Video Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Video Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => onChange({ videoUrl, title: e.target.value })}
            placeholder="Enter a descriptive title for this video"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#667eea] focus:border-transparent"
          />
        </div>

        {/* Video Description */}

        {/* Video Preview */}
        {isValidUrl && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preview
            </label>
            <div className="relative w-full h-64 bg-gray-100 rounded-lg overflow-hidden">
              <iframe
                src={embedUrl}
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={title || 'Video preview'}
              />
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={onSave}
            className="flex items-center space-x-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
          >
            <Save className="w-4 h-4" />
            <span className="text-sm font-medium">Save</span>
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default VideoStep;
